import { WPSClient, RequestContext, APIClient, Cache, SPSClient, Logger } from '@utils'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { getStringsInput } from '../components/localizer/localizer'
import { getPreferencesInput } from './preferences'
import { StringBundle } from '../components/localizer/stringBundle'
import { COUNTER_PACKAGE_PREFIX, safeBoolean } from './utils'

export interface IStringResponse {
  stringMap: WpsRuntime.atv.wps.StringMap
  stringBundle: StringBundle
}

export interface IPreferenceResponse {
  preferencesResponse: WpsRuntime.atv.wps.GetPreferencesOutputProps
}

interface IRequestWpsData<T extends object> {
  stringBundle?: StringBundle
  requestPreferences?: boolean
  context: RequestContext
  api: APIClient
  operation: (client: WPSClient) => Promise<T>
  titleID: string
  enableStarlightCache?: boolean
  logger?: Logger
  requestSPS?: boolean
}

const TEN_MIN_IN_SEC = 10 * 60
const GET_STRINGS_CHUNK_SIZE = 30;
const starlightCache = new Cache({ ttl: TEN_MIN_IN_SEC, limit: 100 });

export const requestServiceData = async <T extends object>({
  context,
  api,
  operation,
  stringBundle,
  titleID,
  logger,
  enableStarlightCache = false,
  requestSPS = false,
  requestPreferences = false,
}: IRequestWpsData<T>) => {
  const wpsClient = new WPSClient(api)
  const spsClient = new SPSClient(api)

  let stringsFuture
  if (stringBundle !== undefined) {
    const stringsInput = getStringsInput(context, stringBundle)

    if (global.__NATIVEAPP__) {
      // Request GetStrings by chunks to stay under the GET URL character limit
      let i, j, idsChunk
      const stringsCalls:Promise<WpsRuntime.atv.wps.GetStringsOutputProps>[] = []
      for (i = 0, j = stringsInput.ids.length; i < j; i += GET_STRINGS_CHUNK_SIZE) {
        idsChunk = stringsInput.ids.slice(i, i + GET_STRINGS_CHUNK_SIZE);
        stringsCalls.push(wpsClient.GetStrings({ context, ids: idsChunk }))
      }

      const localStorageKey = `Detail-GetStrings-${context.locale}-${stringBundle}`
      stringsFuture = () => new Promise<WpsRuntime.atv.wps.StringMap>((resolve, reject) => {
        Promise.all(stringsCalls).then(stringsResponses => {
          const results = Object.assign({}, ...stringsResponses.map(stringsResponse => stringsResponse.results))
          localStorage.setItem(localStorageKey, JSON.stringify(results))

          resolve(results)
        }).catch(error => {
          const resultsFallback = localStorage.getItem(localStorageKey)

          if (resultsFallback) {
            const fallbackMessage = `Return strings with fallback for bundle ${stringBundle} due to ${error}`

            logger && logger.warn(fallbackMessage).count(`${COUNTER_PACKAGE_PREFIX}::GetStrings::Fallback`, 1, false)
            resolve(JSON.parse(resultsFallback))
          } else {
            const failedMessage = `Failed to get strings for bundle ${stringBundle} due to ${error}`

            logger && logger.error(failedMessage).count(`${COUNTER_PACKAGE_PREFIX}::GetStrings::Failed`, 1, false)
            reject(failedMessage)
          }
        })
      })
    } else {
      stringsFuture = () => wpsClient.GetStrings(getStringsInput(context, stringBundle)).then(stringsResponse =>
        stringsResponse.results
      )
    }
  } else {
    stringsFuture = () => Promise.resolve(undefined)
  }

  const preferencesFuture = () => (
    requestPreferences
      ? wpsClient.GetPreferences(getPreferencesInput(context))
      : Promise.resolve(undefined)
  )

  const spsFuture = (pageId: string) => (
    requestSPS ?
      spsClient.GetStaticPageContent({
        pageId,
        marketplaceId: context.marketplaceID,
        locale: context.locale
      }, context)
        .catch(e => {
          const message = `SPS call failed with error: ${e}`
          const label = `${COUNTER_PACKAGE_PREFIX}::SPS::Failed`
          logger && logger.error(message).count(label, 1, false)
        })
      : Promise.resolve(undefined)
  )

  const starlightGti = starlightCache.get(titleID)

  if (starlightGti && enableStarlightCache && requestSPS) {
    logger && logger.count(`${COUNTER_PACKAGE_PREFIX}::Starlight::Title::LookUp::CacheHit`, 1, false)
    const [stringMap, preferencesResponse, operationResponse, spsResponse] = await Promise.all([
      stringsFuture(),
      preferencesFuture(),
      operation(wpsClient),
      spsFuture(starlightGti),
    ])

    return {
      operationResponse,
      stringMap,
      preferencesResponse,
      stringBundle,
      spsResponse
    }
  } else {
    const [stringMap, preferencesResponse, operationResponse] = await Promise.all([
      stringsFuture(),
      preferencesFuture(),
      operation(wpsClient),
    ])

    let spsResponse
    if (requestSPS) {
      const detailPageResponse = operationResponse as WpsRuntime.atv.wps.GetDetailPageOutputProps
      const widgets = detailPageResponse && detailPageResponse.widgets
      const detail = widgets && widgets.productDetails && widgets.productDetails.detail
      const isStarlightEnhanced = safeBoolean(detail && detail.isStarlightEnhanced)
      const catalogId = detail && detail.catalogId
      const disableExploreTab = widgets && widgets.pageContext.features['disableExploreTab'] === 'true'
      const shouldCallSPS = Boolean(!disableExploreTab && isStarlightEnhanced && catalogId)

      spsResponse = shouldCallSPS ? await spsFuture(catalogId) : undefined

      if (spsResponse && enableStarlightCache) {
        logger && logger.count(`${COUNTER_PACKAGE_PREFIX}::Starlight::Title::LookUp::CacheHit`, 0, false)
        starlightCache.put(titleID, catalogId)
      }
    }

    return {
      operationResponse,
      stringMap,
      preferencesResponse,
      stringBundle,
      spsResponse
    }
  }
}
